/* ==========================================================================
   Scuffers 1:1 — Cart drawer behavior (owned by B2)

   theme.js (shared, read-only) already:
     - opens the drawer on add-to-cart success (adds .is-open + re-renders
       .cart-drawer--wrapper_inner from /?sections=cart-drawer)
     - binds [data-cart-toggle]/[data-cart-close] and the overlay at load

   This file adds, without touching theme.js:
     - window.SadyCart.open()/close()/refresh()
     - delegated close (survives inner re-renders that kill theme.js's
       direct listener on the X button)
     - qty stepper / remove / qty input via /cart/change.js + section
       re-render (no page reload; drawer stays open when emptied)
     - upsell quick-add ([data-scart-add]) via /cart/add.js
     - upsell collapse toggle
     - Escape does NOT close the drawer (deliberate, per Scuffers):
       capture-phase blocker stops theme.js's Escape handler.
   ========================================================================== */
(function () {
  'use strict';

  var container = document.getElementById('cart-drawer');
  if (!container) return;

  var SECTION_ID = 'cart-drawer';
  var busy = false;

  function open() {
    container.classList.add('is-open');
    document.body.classList.add('no-scroll');
  }

  function close() {
    container.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
  }

  function setBusy(state) {
    busy = state;
    container.classList.toggle('is-busy', state);
  }

  /* Re-render the drawer inner from the Section Rendering API and
     refresh header cart count bubbles. */
  function refresh() {
    return fetch('/?sections=' + SECTION_ID)
      .then(function (res) { return res.json(); })
      .then(function (sections) {
        var html = sections[SECTION_ID];
        if (!html) return;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('.cart-drawer--wrapper_inner');
        var current = container.querySelector('.cart-drawer--wrapper_inner');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      })
      .then(function () { return fetch('/cart.js'); })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        return cart;
      })
      .catch(function (err) {
        console.error('Cart drawer refresh error:', err);
      });
  }

  function changeLine(key, quantity) {
    if (busy) return;
    setBusy(true);
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/change failed: ' + res.status);
        return refresh();
      })
      .catch(function (err) { console.error('Cart update error:', err); })
      .finally(function () { setBusy(false); });
    /* Drawer deliberately stays open — removing the last item switches
       the re-rendered content to the empty state. */
  }

  function addVariant(variantId) {
    if (busy) return;
    setBusy(true);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart/add failed: ' + res.status);
        return refresh();
      })
      .then(open)
      .catch(function (err) { console.error('Quick add error:', err); })
      .finally(function () { setBusy(false); });
  }

  /* ---------- Delegated clicks (survive inner re-renders) ---------- */
  document.addEventListener('click', function (e) {
    if (!container.contains(e.target)) return;

    /* Close (X button — re-rendered copies lose theme.js's direct listener) */
    var closer = e.target.closest('[data-cart-close]');
    if (closer) {
      e.preventDefault();
      close();
      return;
    }

    /* Add-more ghost tile: navigate if a link is configured, else just
       close the drawer so the customer keeps browsing where she was. */
    var addMore = e.target.closest('[data-add-more]');
    if (addMore) {
      e.preventDefault();
      var addMoreLink = addMore.getAttribute('data-add-more-link');
      if (addMoreLink) {
        window.location.href = addMoreLink;
      } else {
        close();
      }
      return;
    }

    /* Qty +/− */
    var stepper = e.target.closest('.js-update-item');
    if (stepper) {
      e.preventDefault();
      var row = stepper.closest('[data-cart-item]');
      if (!row) return;
      var qty = parseInt(stepper.getAttribute('data-quantity'), 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      changeLine(row.getAttribute('data-key'), qty);
      return;
    }

    /* Remove (trash / corner X). Quantity 0 = remove. */
    var remover = e.target.closest('.js-remove-item');
    if (remover) {
      e.preventDefault();
      var removeRow = remover.closest('[data-cart-item]');
      if (!removeRow) return;
      changeLine(removeRow.getAttribute('data-key'), 0);
      return;
    }

    /* Upsell quick-add */
    var adder = e.target.closest('[data-scart-add]');
    if (adder) {
      e.preventDefault();
      e.stopPropagation();
      addVariant(adder.getAttribute('data-variant-id'));
      return;
    }

    /* Upsell collapse chevron */
    var toggle = e.target.closest('[data-crosselling-toggle]');
    if (toggle) {
      e.preventDefault();
      var box = toggle.closest('[data-crosselling]');
      if (box) box.classList.toggle('is-collapsed');
    }
  });

  /* ---------- Qty input (type a number) ---------- */
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!container.contains(input) || !input.classList.contains('select-on-focus')) return;
    var row = input.closest('[data-cart-item]');
    if (!row) return;
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    changeLine(row.getAttribute('data-key'), qty);
  });

  document.addEventListener('focusin', function (e) {
    if (container.contains(e.target) && e.target.classList.contains('select-on-focus')) {
      e.target.select();
    }
  });

  /* ---------- Escape must NOT close the drawer (per Scuffers) ----------
     theme.js has a document-level bubble listener that closes on Escape.
     Intercept in capture phase while the drawer is open so it never fires. */
  window.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && container.classList.contains('is-open')) {
      e.stopPropagation();
    }
  }, true);

  /* ---------- Public API ---------- */
  window.SadyCart = {
    open: open,
    close: close,
    refresh: refresh
  };
})();
